package org.zq.astrock_ai.ui.components

import org.zq.astrock_ai.theme.LocalAppFontFamily

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.astrock_ai.data.MockStockData
import org.zq.astrock_ai.theme.BinanceColors

@Composable
fun PriceChart(
    chartSeed: Int = 0,
    compact: Boolean = false,
    historyCloses: List<Float>? = null,
    modifier: Modifier = Modifier,
) {
    val (history, prediction) = remember(chartSeed, historyCloses) {
        if (historyCloses != null && historyCloses.size >= 2) {
            val tail = historyCloses.takeLast(60)
            val last = tail.last()
            val prediction = List(10) { i -> last * (1f + (i + 1) * 0.002f * if (chartSeed % 2 == 0) 1 else -1) }
            tail to prediction
        } else {
            MockStockData.generateChartData(chartSeed)
        }
    }
    val allPoints = history + prediction
    val minY = allPoints.min() * 0.995f
    val maxY = allPoints.max() * 1.005f
    val chartHeight = if (compact) 260.dp else 420.dp

    Box(
        modifier = modifier
            .fillMaxWidth()
            .height(chartHeight)
            .clip(RoundedCornerShape(8.dp))
            .background(BinanceColors.Dark.copy(alpha = 0.5f))
            .border(1.dp, BinanceColors.Hairline.copy(alpha = 0.5f), RoundedCornerShape(8.dp)),
    ) {
        Canvas(modifier = Modifier.fillMaxSize().padding(16.dp)) {
            val w = size.width
            val h = size.height
            val historyCount = history.size
            val totalCount = historyCount + prediction.size

            fun yFor(value: Float) = h - (value - minY) / (maxY - minY) * h
            fun xFor(index: Int) = index.toFloat() / (totalCount - 1) * w

            for (i in 0..4) {
                val y = h * i / 4
                drawLine(BinanceColors.Hairline.copy(alpha = 0.5f), Offset(0f, y), Offset(w, y), 1f)
            }

            val historyPath = Path()
            history.forEachIndexed { i, v ->
                val x = xFor(i)
                val y = yFor(v)
                if (i == 0) historyPath.moveTo(x, y) else historyPath.lineTo(x, y)
            }
            drawPath(historyPath, BinanceColors.Yellow, style = Stroke(width = 2f, cap = StrokeCap.Round))

            val fillPath = Path().apply {
                addPath(historyPath)
                lineTo(xFor(historyCount - 1), h)
                lineTo(0f, h)
                close()
            }
            drawPath(fillPath, Brush.verticalGradient(listOf(BinanceColors.Yellow.copy(0.08f), Color.Transparent)))

            val predPath = Path()
            val startX = xFor(historyCount - 1)
            val startY = yFor(history.last())
            predPath.moveTo(startX, startY)
            prediction.forEachIndexed { i, v ->
                predPath.lineTo(xFor(historyCount + i), yFor(v))
            }
            drawPath(
                predPath,
                BinanceColors.Green,
                style = Stroke(width = 2f, cap = StrokeCap.Round, pathEffect = androidx.compose.ui.graphics.PathEffect.dashPathEffect(floatArrayOf(12f, 8f))),
            )
        }

        if (compact) {
            Row(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState())
                    .padding(horizontal = 12.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                MaLabel("MA5", "1,672.34", BinanceColors.Yellow)
                MaLabel("MA10", "1,658.92", BinanceColors.Info)
                MaLabel("MA20", "1,645.18", BinanceColors.Green)
                MaLabel("MA60", "1,623.56", BinanceColors.Red)
            }
        } else {
            Row(
                modifier = Modifier.align(Alignment.TopStart).padding(16.dp),
                horizontalArrangement = Arrangement.spacedBy(24.dp),
            ) {
                MaLabel("MA5", "1,672.34", BinanceColors.Yellow)
                MaLabel("MA10", "1,658.92", BinanceColors.Info)
                MaLabel("MA20", "1,645.18", BinanceColors.Green)
                MaLabel("MA60", "1,623.56", BinanceColors.Red)
            }
        }

        Row(
            modifier = Modifier
                .align(if (compact) Alignment.TopEnd else Alignment.TopEnd)
                .padding(if (compact) 8.dp else 16.dp)
                .clip(RoundedCornerShape(8.dp))
                .background(BinanceColors.Yellow.copy(alpha = 0.1f))
                .border(1.dp, BinanceColors.Yellow.copy(alpha = 0.3f), RoundedCornerShape(8.dp))
                .padding(horizontal = 12.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Box(Modifier.size(8.dp).clip(RoundedCornerShape(4.dp)).background(BinanceColors.Yellow))
            Text("预测区间", fontSize = 13.sp, fontWeight = FontWeight.Medium, color = BinanceColors.Yellow)
        }
    }
}

@Composable
private fun MaLabel(label: String, value: String, color: Color) {
    Row {
        Text("$label: ", fontSize = 13.sp, color = BinanceColors.Muted)
        Text(value, fontSize = 13.sp, fontFamily = LocalAppFontFamily.current, color = color)
    }
}

@Composable
fun VolumeBars(bars: List<Float> = MockStockData.volumeBars, compact: Boolean = false) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(if (compact) 48.dp else 64.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(BinanceColors.Dark.copy(alpha = 0.3f))
            .border(1.dp, BinanceColors.Hairline.copy(alpha = 0.3f), RoundedCornerShape(8.dp))
            .padding(horizontal = 8.dp, vertical = 8.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxSize(),
            horizontalArrangement = Arrangement.spacedBy(1.dp),
            verticalAlignment = Alignment.Bottom,
        ) {
            bars.forEachIndexed { index, height ->
                val color = when {
                    index >= bars.size - 6 -> BinanceColors.Yellow.copy(alpha = 0.3f + (index - bars.size + 6) * 0.1f)
                    index % 3 == 1 -> BinanceColors.Red.copy(alpha = 0.6f)
                    else -> BinanceColors.Green.copy(alpha = 0.6f)
                }
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight(height)
                        .clip(RoundedCornerShape(topStart = 2.dp, topEnd = 2.dp))
                        .background(color),
                )
            }
        }
        Text(
            "成交量 (万手)",
            fontSize = 12.sp,
            color = BinanceColors.Muted,
            modifier = Modifier.align(Alignment.BottomStart).padding(start = 4.dp, bottom = 2.dp),
        )
    }
}

@Composable
fun ChartSection(
    chartSeed: Int = 0,
    compact: Boolean = false,
    historyCloses: List<Float>? = null,
) {
    GlassPanel(modifier = Modifier.fillMaxWidth().padding(bottom = 0.dp)) {
        Column(Modifier.padding(if (compact) 12.dp else 20.dp)) {
            if (compact) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    listOf("K线图", "分时", "五日", "技术指标").forEachIndexed { i, tab ->
                        Text(
                            tab,
                            fontSize = 13.sp,
                            fontWeight = if (i == 0) FontWeight.Medium else FontWeight.Normal,
                            color = if (i == 0) BinanceColors.Body else BinanceColors.Body.copy(alpha = 0.85f),
                            modifier = Modifier.clickable { }.padding(horizontal = 12.dp, vertical = 6.dp),
                        )
                    }
                }
                Spacer(Modifier.height(8.dp))
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState())
                        .clip(RoundedCornerShape(8.dp))
                        .background(BinanceColors.Elevated)
                        .padding(4.dp),
                    horizontalArrangement = Arrangement.spacedBy(2.dp),
                ) {
                    listOf("日K", "周K", "月K", "60分", "30分").forEachIndexed { i, t ->
                        Text(
                            t,
                            fontSize = 12.sp,
                            fontWeight = if (i == 0) FontWeight.SemiBold else FontWeight.Normal,
                            color = if (i == 0) BinanceColors.DarkText else BinanceColors.Body.copy(alpha = 0.85f),
                            modifier = Modifier
                                .clip(RoundedCornerShape(6.dp))
                                .background(if (i == 0) BinanceColors.Yellow else Color.Transparent)
                                .clickable { }
                                .padding(horizontal = 10.dp, vertical = 5.dp),
                        )
                    }
                }
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row {
                        listOf("K线图", "分时", "五日", "技术指标").forEachIndexed { i, tab ->
                            Text(
                                tab,
                                fontSize = 14.sp,
                                fontWeight = if (i == 0) FontWeight.Medium else FontWeight.Normal,
                                color = if (i == 0) BinanceColors.Body else BinanceColors.Body.copy(alpha = 0.85f),
                                modifier = Modifier.clickable { }.padding(horizontal = 16.dp, vertical = 8.dp),
                            )
                        }
                    }
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(8.dp))
                            .background(BinanceColors.Elevated)
                            .padding(4.dp),
                        horizontalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        listOf("日K", "周K", "月K", "60分", "30分").forEachIndexed { i, t ->
                            Text(
                                t,
                                fontSize = 13.sp,
                                fontWeight = if (i == 0) FontWeight.SemiBold else FontWeight.Normal,
                                color = if (i == 0) BinanceColors.DarkText else BinanceColors.Body.copy(alpha = 0.85f),
                                modifier = Modifier
                                    .clip(RoundedCornerShape(6.dp))
                                    .background(if (i == 0) BinanceColors.Yellow else Color.Transparent)
                                    .clickable { }
                                    .padding(horizontal = 12.dp, vertical = 6.dp),
                            )
                        }
                    }
                }
            }
            Spacer(Modifier.height(if (compact) 12.dp else 20.dp))
            PriceChart(chartSeed = chartSeed, compact = compact, historyCloses = historyCloses)
            Spacer(Modifier.height(12.dp))
            VolumeBars(compact = compact)
        }
    }
}
